import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { Minus, Plus, Trash2, ArrowRight, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { useCart, formatPrice } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CartPage() {
  const { items, total, setQty, remove } = useCart();
  const [shopifyReady, setShopifyReady] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [sampleOn, setSampleOn] = useState(false);
  const [sample, setSample] = useState("jasmin");

  useEffect(() => {
    axios
      .get(`${API}/shopify/status`)
      .then((res) => setShopifyReady(Boolean(res.data.configured)))
      .catch(() => setShopifyReady(false));
  }, []);

  const checkout = async () => {
    if (!shopifyReady) {
      toast.info("Demo-versio: Shopify-kassa kytketään, kun kaupan tiedot on lisätty.");
      return;
    }
    setRedirecting(true);
    try {
      const res = await axios.post(`${API}/shopify/checkout`, {
        items: items.map((i) => ({ slug: i.slug, qty: i.qty })),
        sample: sampleOn ? sample : null,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kassalle siirtyminen epäonnistui. Yritä uudelleen.");
      setRedirecting(false);
    }
  };

  return (
    <main data-testid="cart-page" className="pt-28 md:pt-36 pb-28">
      <Seo title="Ostoskori – JINSU" description="JINSU-ostoskorisi." />

      <div className="mx-auto max-w-5xl px-5 sm:px-8">
        <p className="eyebrow text-water-deep/80">Ostoskori</p>
        <h1 className="h-section font-heading mt-5 font-semibold text-foreground" lang="fi">
          Ostoskorisi
        </h1>

        {items.length === 0 ? (
          <div className="mt-14 rounded-[2rem] bg-white p-12 text-center shadow-[0_10px_40px_-18px_rgba(23,68,94,0.18)]" data-testid="cart-empty">
            <ShoppingBag size={36} className="mx-auto text-water" strokeWidth={1.5} />
            <p className="mt-5 font-heading text-xl font-semibold text-foreground">
              Ostoskorisi on tyhjä
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              Tutustu suihkusuodattimiin ja valitse omasi.
            </p>
            <Link
              to="/kauppa/"
              data-testid="cart-empty-cta"
              className="mt-7 inline-flex items-center gap-2.5 rounded-full bg-primary px-7 py-3.5 text-sm font-bold text-primary-foreground transition-transform duration-200 hover:scale-[1.04]"
            >
              Siirry kauppaan <ArrowRight size={16} />
            </Link>
          </div>
        ) : (
          <div className="mt-12 grid gap-8 lg:grid-cols-12">
            <ul className="space-y-4 lg:col-span-8" data-testid="cart-items">
              {items.map(({ slug, qty, product }) => (
                <li
                  key={slug}
                  data-testid={`cart-item-${slug}`}
                  className="flex items-center gap-5 rounded-3xl bg-white p-4 md:p-5 shadow-[0_10px_40px_-18px_rgba(23,68,94,0.15)]"
                >
                  <Link to={`/tuote/${slug}/`} className="shrink-0">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="h-20 w-20 rounded-2xl object-cover"
                    />
                  </Link>
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/tuote/${slug}/`}
                      className="block truncate text-sm font-bold text-foreground transition-colors hover:text-primary"
                    >
                      {product.name}
                    </Link>
                    <p className="mt-0.5 text-xs text-muted-foreground">{product.priceLabel} / kpl</p>
                    <div className="mt-3 flex items-center gap-3">
                      <div className="flex items-center rounded-full border border-border">
                        <button
                          onClick={() => setQty(slug, qty - 1)}
                          className="flex h-8 w-8 items-center justify-center text-foreground hover:text-primary"
                          aria-label="Vähennä määrää"
                          data-testid={`cart-decrease-${slug}`}
                        >
                          <Minus size={13} />
                        </button>
                        <span className="w-8 text-center text-xs font-extrabold" data-testid={`cart-qty-${slug}`}>
                          {qty}
                        </span>
                        <button
                          onClick={() => setQty(slug, qty + 1)}
                          className="flex h-8 w-8 items-center justify-center text-foreground hover:text-primary"
                          aria-label="Lisää määrää"
                          data-testid={`cart-increase-${slug}`}
                        >
                          <Plus size={13} />
                        </button>
                      </div>
                      <button
                        onClick={() => remove(slug)}
                        className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600"
                        aria-label={`Poista ${product.name} ostoskorista`}
                        data-testid={`cart-remove-${slug}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                  <p className="shrink-0 text-sm font-extrabold text-foreground" data-testid={`cart-line-total-${slug}`}>
                    {formatPrice(product.price * qty)}
                  </p>
                </li>
              ))}
            </ul>

            <aside className="lg:col-span-4">
              <div className="rounded-[2rem] bg-water-soft p-7" data-testid="cart-summary">
                <h2 className="font-heading text-xl font-semibold text-foreground">Yhteenveto</h2>
                <div className="mt-5 flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Välisumma (sis. ALV)</span>
                  <span className="font-extrabold text-foreground" data-testid="cart-total">
                    {formatPrice(total)}
                  </span>
                </div>
                <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
                  Toimituskulut määräytyvät tilauksen mukaan ja lisätään kassalla. Toimitus Postin
                  noutopisteeseen tai pakettiautomaattiin.
                </p>

                {/* Free scent sample */}
                <div className="mt-6 rounded-2xl bg-white/70 p-4" data-testid="scent-sample">
                  <label className="flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      checked={sampleOn}
                      onChange={(e) => setSampleOn(e.target.checked)}
                      className="mt-1 h-4 w-4 accent-[hsl(var(--primary))]"
                      data-testid="sample-checkbox"
                    />
                    <span>
                      <span className="block text-sm font-bold text-foreground">
                        Ilmainen tuoksunäyte tilaukseen
                      </span>
                      <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
                        Liitämme mukaan valitsemasi vitamiinisuodattimen tuoksunäytteen.
                      </span>
                    </span>
                  </label>
                  {sampleOn && (
                    <div className="mt-3 flex gap-2 pl-7" data-testid="sample-choices">
                      <button
                        type="button"
                        onClick={() => setSample("jasmin")}
                        data-testid="sample-jasmin"
                        className={`rounded-full px-4 py-2 text-xs font-bold transition-all duration-200 ${
                          sample === "jasmin"
                            ? "bg-jasmin text-jasmin-deep ring-2 ring-[hsl(var(--jasmin-deep))]"
                            : "bg-white text-muted-foreground hover:bg-jasmin/60"
                        }`}
                      >
                        Jasmin
                      </button>
                      <button
                        type="button"
                        onClick={() => setSample("mandariini")}
                        data-testid="sample-mandariini"
                        className={`rounded-full px-4 py-2 text-xs font-bold transition-all duration-200 ${
                          sample === "mandariini"
                            ? "bg-mandariini text-mandariini-deep ring-2 ring-[hsl(var(--mandariini-deep))]"
                            : "bg-white text-muted-foreground hover:bg-mandariini/60"
                        }`}
                      >
                        Mandariini
                      </button>
                    </div>
                  )}
                </div>

                <button
                  onClick={checkout}
                  disabled={redirecting}
                  data-testid="checkout-button"
                  className="mt-6 flex w-full items-center justify-center gap-2.5 rounded-full bg-primary px-7 py-3.5 text-sm font-bold text-primary-foreground shadow-lg shadow-primary/25 transition-transform duration-200 hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100"
                >
                  {redirecting ? "Ohjataan Shopifyyn…" : "Siirry kassalle"} <ArrowRight size={16} />
                </button>
                <Link
                  to="/kauppa/"
                  className="mt-4 block text-center text-xs font-bold text-primary transition-colors hover:text-water-deep"
                  data-testid="cart-continue-shopping"
                >
                  Jatka ostoksia
                </Link>
              </div>
            </aside>
          </div>
        )}
      </div>
    </main>
  );
}
